import classNames from 'classnames';
import gsap from 'gsap';
import msToHms from 'ms-to-hms';
import type { ReactElement, Ref, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import PlayIcon from 'components/atoms/play-icon/PlayIcon';
import Icon from 'components/atoms/icon/Icon';
import type { VideoControlsProps, VideoControlsRef } from './VideoControls.data';
import styles from './VideoControls.module.scss';
import {
  seek,
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
} from './VideoControls.transitions';
import { useDraggable } from '../../../hooks/useDraggable';
import Text from '../../atoms/text/Text';
import {
  useMountTransition,
  useTransitionController,
  useUnMountTransition,
} from '../../../util/react-transition-component';
import { useChangeTransitionState } from '../../../util/react-transition-component/hooks/useChangeTransitionState';
import { renderCta } from '../../../util/cta/cta.template';
import useEventListener from '../../../hooks/useEventListener';

export default function VideoControls({
  isVisible,
  transitionOnMount = false,
  transitionOnUnMount = false,
  onProgressUpdate,
  onTogglePlay,
  onToggleMute,
  onToggleFullScreen,
  className,
  progress = 0,
  duration = 0,
  isMuted = false,
  isPlaying = false,
  isFullscreen = false,
  enableFullscreenButton = true,
  exitFullScreen,
  ref,
}: VideoControlsProps & { ref?: Ref<VideoControlsRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const progressBarRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const knobRef = useRef<HTMLDivElement>(null);

  const [currentTime, setCurrentTime] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [currentProgress, setCurrentProgress] = useState(0);
  const isHidden = useRef<boolean>(true);

  const dragger = useRef<Draggable | null>(null);

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      onStart(direction) {
        isHidden.current = direction === 'out';
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, isVisible);
  useMountTransition(transitionController, transitionOnMount);
  useUnMountTransition(transitionController, transitionOnUnMount);

  const updateProgress = useCallback(
    (progress: number) => {
      if (isHidden.current) return;
      setCurrentProgress(progress);
      onProgressUpdate(progress);
    },
    [onProgressUpdate],
  );

  const onBarPress = useCallback(() => {
    if (dragger.current && progressBarRef.current) {
      const newPosition =
        dragger.current.pointerX - progressBarRef.current.getBoundingClientRect().x;
      updateProgress(newPosition / dragger.current.maxX);
      gsap.set(knobRef.current, { x: newPosition });
      dragger.current.update();
    }
  }, [dragger, progressBarRef, knobRef, updateProgress]);

  const onDrag = useCallback(() => {
    if (dragger.current) {
      updateProgress(dragger.current.x / dragger.current.maxX);
    }
  }, [dragger, updateProgress]);

  dragger.current = useDraggable(
    knobRef,
    {
      type: 'x',
      edgeResistance: 1,
      lockAxis: true,
      cursor: 'ew-resize',
      onDrag,
      onPress: onBarPress,
    },
    {
      trigger: progressBarRef,
      bounds: progressBarRef,
    },
  );

  useEffect(() => {
    if (isHidden.current) return;
    setCurrentProgress(progress);
    if (dragger.current) {
      gsap.set(knobRef.current, { x: progress * dragger.current.maxX });
    }
  }, [progress, knobRef, dragger]);

  useEffect(() => {
    if (isHidden.current) return;
    setCurrentTime(msToHms(progress * duration * 1000));
  }, [progress, duration]);

  useEffect(() => {
    setTotalTime(msToHms(duration * 1000));
  }, [duration]);

  useEffect(() => {
    if (isHidden.current) return;
    seek(currentProgress, progressRef);
  }, [currentProgress, progressRef]);

  const onKeydown = ({ key }: KeyboardEvent): void => {
    if (typeof exitFullScreen !== 'function') return;
    if (key === 'Escape' && isFullscreen) {
      exitFullScreen();
    }
  };

  useEventListener('keydown', onKeydown);

  const toggleFullScreen = useCallback(() => {
    if (isFullscreen) {
      if (exitFullScreen) {
        exitFullScreen();
      } else {
        onToggleFullScreen();
      }
    } else {
      onToggleFullScreen();
    }
  }, [isFullscreen, exitFullScreen, onToggleFullScreen]);

  return (
    <div
      ref={elementRef}
      className={classNames(styles.videoControls, className, {
        [styles.isFullscreen]: isFullscreen,
      })}
    >
      {renderCta({
        innerTemplate: <Icon name="close" className={classNames(styles.icon)} />,
        props: {
          className: classNames(styles.closeButton),
          onClick: exitFullScreen,
          'aria-label': 'Click to close',
        },
      })}

      <div className={classNames(styles.bottomControls, className)}>
        <div className={classNames(styles.leftControls)}>
          <button
            aria-label="toggle play"
            type="button"
            className={classNames(styles.playButton, styles.button, 'button')}
            onClick={onTogglePlay}
          >
            <PlayIcon isPlaying={isPlaying} />
          </button>

          <button
            type="button"
            aria-label="toggle mute"
            className={classNames(styles.muteButton, styles.button, 'button')}
            onClick={onToggleMute}
          >
            <Icon className={styles.muteIcon} name={`${isMuted ? 'sound-off' : 'sound-on'}`} />
          </button>

          <Text
            as="time"
            size="body-standard"
            transitionInType="none"
            className={classNames(styles.time, styles.currentTime)}
          >
            {currentTime}
          </Text>
        </div>

        <div ref={progressBarRef} className={classNames(styles.progressBar)}>
          <div ref={knobRef} className={classNames(styles.knob)} />
          <span ref={progressRef} className={classNames(styles.progress, 'abs-fill')} />
        </div>

        <div className={classNames(styles.rightControls)}>
          <Text
            as="time"
            size="body-standard"
            transitionInType="none"
            className={classNames(styles.time, styles.totalTime)}
          >
            {totalTime}
          </Text>

          {enableFullscreenButton && (
            <button
              aria-label="toggle fullscreen"
              type="button"
              className={classNames(styles.fullscreenButton, styles.button, 'button')}
              onClick={toggleFullScreen}
            >
              <Icon
                className={styles.fullscreenIcon}
                name={isFullscreen ? 'fullscreen-off' : 'fullscreen-on'}
              />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
